import { action, computed, makeObservable, observable } from 'mobx'
import { arrayMove, SortEnd } from 'react-sortable-hoc'

import { IGlobeViewMapFile, ITileset, MapFileType } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { LOCAL_STORAGE_TILESET_NOTIFICATION_PREFIX } from '~/client/src/shared/components/MapBoxViewer/components/TilesetNotificationCard'
import RemoteTilesetsStore from '~/client/src/shared/stores/domain/RemoteTilesets.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import { isTilesetReady } from '~/client/src/shared/utils/tilesetReadiness'

import MapViewSetUpStore from '../../MapViewSetUp.store'
import { ViewType } from '../../stores/MapBoxEditor.store'

export interface IMapFile {
  type: MapFileType
  name: string
  fileName: string
  id: string
  filledImage?: string
}

export default class GlobeLeftPanelStore {
  @observable public isMapNotificationModalOpened: boolean = false
  @observable public hiddenNotificationIds: Set<string> = new Set()

  public constructor(
    private readonly mapViewSetUpStore: MapViewSetUpStore,
    private readonly sitemapsStore: SitemapsStore,
    private readonly tilesetsStore: TilesetsStore,
    private readonly remoteTilesetsStore: RemoteTilesetsStore,
    private readonly state: DesktopInitialState,
  ) {
    makeObservable(this)
    this.initHiddenNotifications()
  }

  private initHiddenNotifications(): void {
    // Load hidden notification IDs from localStorage
    this.tilesetsStore.list.forEach(t => {
      const value = localStorage.getItem(
        LOCAL_STORAGE_TILESET_NOTIFICATION_PREFIX + t.id,
      )
      if (value === 'true') {
        this.hiddenNotificationIds.add(t.id)
      }
    })
  }

  @action.bound
  public hideNotification(tilesetId: string): void {
    localStorage.setItem(
      LOCAL_STORAGE_TILESET_NOTIFICATION_PREFIX + tilesetId,
      'true',
    )
    this.hiddenNotificationIds.add(tilesetId)
  }

  public isMapFileDisplayed = (mapFile: IMapFile): boolean => {
    switch (mapFile.type) {
      case MapFileType.Sitemap:
        return this.isPlanDisplayed(mapFile.id)
      case MapFileType.Tileset:
        return this.isTilesetDisplayed(mapFile.id)
      case MapFileType.RemoteTileset:
        return this.isRemoteTilesetDisplayed(mapFile.id)
      default:
        return false
    }
  }

  @action.bound
  public toggleIsMapNotificationModalOpened(): void {
    this.isMapNotificationModalOpened = !this.isMapNotificationModalOpened
  }

  public isPlanDisplayed = (planId: string): boolean => {
    const {
      mapBoxEditorStore: { isRubberMode, leftPanelVisibility },
      globeViewSetupStore: { selectedGlobeView },
    } = this.mapViewSetUpStore
    return isRubberMode
      ? leftPanelVisibility.plans[planId]
      : selectedGlobeView?.sitemapsMap?.[planId]
  }

  public isTilesetDisplayed = (tilesetId: string): boolean => {
    const {
      mapBoxEditorStore: { isRubberMode, leftPanelVisibility },
      globeViewSetupStore: { selectedGlobeView },
    } = this.mapViewSetUpStore
    return isRubberMode
      ? leftPanelVisibility.tilesets[tilesetId]
      : selectedGlobeView?.tilesetsMap?.[tilesetId]
  }

  public isRemoteTilesetDisplayed = (remoteTilesetId: string): boolean => {
    const {
      mapBoxEditorStore: { isRubberMode, leftPanelVisibility },
      globeViewSetupStore: { selectedGlobeView },
    } = this.mapViewSetUpStore
    return isRubberMode
      ? leftPanelVisibility.remoteTilesets[remoteTilesetId]
      : selectedGlobeView?.remoteTilesetsMap?.[remoteTilesetId]
  }

  @computed
  public get availableMapFiles(): IMapFile[] {
    return [
      ...this.referencedPlans,
      ...this.tilesetsList,
      ...this.remoteTilesetsList,
    ]
  }

  @computed
  private get referencedPlans(): IMapFile[] {
    return this.sitemapsStore.list
      .filter(x => x.isReferenced)
      .map(s => ({
        type: MapFileType.Sitemap,
        name: s.name,
        id: s.id,
        filledImage: s.filledImage,
        fileName: this.state.basemaps.get(s.basemapId)?.fileName || s.name,
      }))
  }

  @computed
  private get tilesetsList(): IMapFile[] {
    return this.state.isTilesetsDisabled
      ? []
      : this.state.activeTilesetsList.filter(isTilesetReady).map(t => ({
          type: MapFileType.Tileset,
          id: t.id,
          name: t.mapboxData.name,
          fileName: t.mapboxData.name,
        }))
  }

  @computed
  private get remoteTilesetsList(): IMapFile[] {
    return this.state.isTilesetsDisabled
      ? []
      : this.remoteTilesetsStore.list.map(t => ({
          type: MapFileType.RemoteTileset,
          id: t.id,
          name: t.name || '',
          fileName: t.name || '',
        }))
  }

  public get filteredMapFiles(): IMapFile[] {
    const { searchKey } = this.mapViewSetUpStore.mapViewItemsSetupStore
    if (!searchKey) {
      return this.availableMapFiles
    }
    return this.availableMapFiles.filter(x =>
      x.name.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase()),
    )
  }

  @computed
  public get filteredGlobeViewMapFiles(): IMapFile[] {
    if (!this.mapViewSetUpStore.globeViewSetupStore.selectedGlobeView) {
      return []
    }
    return this.mapViewSetUpStore.globeViewSetupStore.selectedGlobeView.mapFiles
      .filter(f => !!this.filteredMapFileIdToMapFile[f.entityType + f.entityId])
      .map(f => this.filteredMapFileIdToMapFile[f.entityType + f.entityId])
      .reverse()
  }

  @computed
  public get filteredMapFileIdToMapFile(): {
    [planId: string]: IMapFile
  } {
    return this.filteredMapFiles.reduce((acc, f) => {
      acc[f.type + f.id] = f
      return acc
    }, {})
  }

  public getVisibleObjectsCount = (selectedViewType: ViewType): number => {
    const { shownMapViewItems } = this.mapViewSetUpStore.mapViewItemsSetupStore

    switch (selectedViewType) {
      case ViewType.Objects:
        return shownMapViewItems.length
      case ViewType.MapFiles:
        return (
          this.mapViewSetUpStore.globeViewSetupStore.selectedGlobeView
            ?.sitemapIds?.length || 0
        )
      default:
        return 0
    }
  }

  public onViewTypeSelect = async (viewType: ViewType) => {
    const {
      onViewTypeSelect,
      currentStep,
      setStep,
      mapBoxEditorStore: { isRubberMode },
    } = this.mapViewSetUpStore

    onViewTypeSelect(viewType)
    if (currentStep && !isRubberMode) {
      setStep(null)
    }
  }

  @action
  public onMapFilesSortEnd = (sort: SortEnd): void => {
    if (sort.oldIndex === sort.newIndex) {
      return
    }
    const newMapFiles = arrayMove(
      this.filteredGlobeViewMapFiles,
      sort.oldIndex,
      sort.newIndex,
    )
      .map(f => ({ entityId: f.id, entityType: f.type }) as IGlobeViewMapFile)
      .reverse()
    this.mapViewSetUpStore.globeViewSetupStore.updateGlobeView(
      null,
      null,
      null,
      newMapFiles,
    )
  }

  @computed
  public get tilesetsForNotifications(): ITileset[] {
    const { sortedList } = this.tilesetsStore
    return sortedList.filter(t => !this.hiddenNotificationIds.has(t.id))
  }
}
